// One task's page: facts, steps, actions, graph, timeline and conversation.
import type { ReactNode } from "react";

import { rich } from "../../../helpers";
import stepDialog from "./stepDialog";

export default {
  loadFailed: "Could not load the task",
  factsLabel: "Task facts",
  parent: "Parent",
  subTasks: "Sub-tasks",
  inGroup: (n: number) => `${n} in the group`,
  link: "Link",
  stoppedAt: "Stopped at",
  runningFor: "Running for",
  queued: (when: string) => `queued ${when}`,
  steps: "Steps",
  stepsDescription: "Outputs are the agent's reported values, visible to the host by design",
  noSteps: "No step has run yet.",
  visit: (n: number) => `visit ${n}`,
  stepVisit: (name: string, n: number) => `${name} · visit ${n}`,
  run: "run",
  retry: "Retry",
  cancelTask: "Cancel task",
  cancelConfirm: "Cancel this task?",
  cancelBody: "Stops the step that is running, or closes the open approval. You can retry it later from the step it stopped on.",
  queuedAgain: "Queued again",
  retryHelp: "Continues from the last step",
  startOver: "Start over",
  startOverConfirm: "Start this task over?",
  startOverBody:
    "Start over runs every step of the workflow again from the beginning and bills them again. Steps so far stay in the history.",
  startedOver: "Started over from the first step",
  cancelled: "Task cancelled",
  parentTask: "Parent task",
  parentHelp: "The task is listed under its parent. Pick none to make it a top-level task again.",
  parentSet: "Parent set",
  parentCleared: "Parent cleared",
  batchLabel: "Question batch",
  batchAsked: (n: number) => `The agent asked ${n} questions together`,
  batchHelp: "Answer each one; the step resumes when the last is answered.",
  batchItem: (id: string) => `item ${id}`,
  batchProgress: (answered: number, size: number) => `${answered} of ${size} answered`,
  questionWaiting: "A question is waiting for an answer",
  batchSummary: (n: number, progress: string) => `${n} questions asked together · ${progress}`,
  conversation: "Conversation",
  conversationDescription: "Step messages and summaries, end-to-end encrypted",
  checkingKeys: "Checking this browser's keys…",
  notReadable: "Not readable in this browser",
  notReadableBody: "This browser was not a recipient when these steps ran. The conversation is readable on the owner's devices that were.",
  openConversation: "Open conversation",
  noConversation: "No conversation yet",
  noConversationBody: "It opens when the task starts; every step adds its messages and summary to it.",
  ownerDevices: "Readable on the owner's devices",
  ownerDevicesBody: "Each step's messages and summary are encrypted in the worker to the owner's devices. The host, and this page, see only the timeline's metadata.",
  linkToRead: "Link this browser to read it",
  linkToReadBody: (link: ReactNode) => rich("The conversation is readable on your devices. ", link, " to read new steps here."),
  linkBrowser: "Link this browser",
  legend: "Legend",
  visited: "Visited",
  onItNow: "On it now",
  graph: "Workflow",
  graphDescription: "The path this task took",
  timeline: "Timeline",
  timelineDescription: "Metadata only: what the host records",
  nothingYet: "Nothing yet",
  questionsEncrypted: "The questions are encrypted to the owner's devices",
  stepStarted: (node: string, visit: number) => `${node} started${visit > 1 ? ` (visit ${visit})` : ""}`,
  agentStep: "Agent step",
  stepResult: (node: string, outcome: string) => `${node}: ${outcome}`,
  tokens: (n: string) => `${n} tokens`,
  approvalAsked: (node: string) => `Approval asked: ${node}`,
  answered: (answer: string) => `Answered “${answer}”`,
  stepDialog,
};
